import { z } from 'zod';
import { isoInstantSchema } from "./event-schemas.js";
/**
 * Trusting a handset to admit people, and withdrawing that trust.
 *
 * Two callers, and the difference between them is the whole design. A
 * supervisor holding `GATE_SUPERVISE` creates an activation and revokes a
 * device; the handset itself claims an activation, and it authenticates with
 * the code rather than with anybody's session — a scanner at a gate is not a
 * person, and requiring it to hold a member's credentials would mean either
 * sharing one login across a venue's handsets or making every scan depend on
 * whoever last signed in.
 *
 * Nothing here decides anything. Every rule — an activation's lifetime, whether
 * a gate belongs to the event, whether a revoked device may return — already
 * lives in `provisioning-service.ts`, which held all of it before any route
 * existed. These schemas are the shape of the conversation, not a second
 * authority.
 */
// --- What a supervisor creates ----------------------------------------------
export const createActivationRequestSchema = z.object({
    /**
     * Which door this handset is going to, when that is known.
     *
     * Null is an ordinary answer rather than a missing one: a venue often
     * provisions a stack of handsets in an office and assigns them to gates on
     * the night. A gate can be set afterwards without reprovisioning.
     */
    gateId: z.string().uuid().nullable().default(null),
    /** What a supervisor will call this handset when they look for it later. */
    label: z.string().trim().min(1).max(120),
});
export const createdActivationSchema = z.object({
    activationId: z.string().uuid(),
    label: z.string(),
    /**
     * The only time this value exists in readable form.
     *
     * Stored as a hash, shown once, and never retrievable — so a leaked database
     * does not hand somebody a working scanner, and a supervisor who loses the
     * code creates another rather than looking the old one up.
     */
    activationCode: z.string(),
    expiresAt: isoInstantSchema,
});
// --- What a handset claims ---------------------------------------------------
export const claimActivationRequestSchema = z.object({
    activationCode: z.string().trim().min(8).max(64),
    /**
     * The public half of the identity this handset just generated, DER
     * SubjectPublicKeyInfo, base64.
     *
     * The private half never leaves the device and the server has no use for it.
     * What the server keeps is the half it needs to recognise a signature, which
     * is why a stolen database cannot impersonate a scanner.
     */
    devicePublicKeySpki: z.string().trim().min(1).max(1000),
    displayName: z.string().trim().min(1).max(120),
});
export const activatedScannerSchema = z.object({
    scannerDeviceId: z.string().uuid(),
    eventId: z.string().uuid(),
    displayName: z.string(),
    /**
     * Short, readable, and shown on both the handset and the supervisor's list.
     *
     * A supervisor matching a physical device to a row should not have to read a
     * public key aloud.
     */
    deviceFingerprint: z.string(),
    gateId: z.string().uuid().nullable(),
});
// --- Withdrawing trust -------------------------------------------------------
/**
 * Why a device is no longer trusted.
 *
 * A closed list, because this is the field a security review reads and free
 * text cannot be counted. A handset that was lost and one that was retired at
 * the end of a season are the same action and very different facts.
 */
export const SCANNER_REVOCATION_REASONS = [
    'LOST_OR_STOLEN',
    'RETIRED',
    'REPLACED',
    'SUSPECTED_COMPROMISE',
];
export const scannerRevocationReasonSchema = z.enum(SCANNER_REVOCATION_REASONS);
export const revokeScannerRequestSchema = z.object({
    reasonCode: scannerRevocationReasonSchema,
});
export const assignGateRequestSchema = z.object({ gateId: z.string().uuid() });
// --- What a supervisor sees --------------------------------------------------
/**
 * One handset, as the person responsible for the gate reads it.
 *
 * Enough to answer "which physical device is this, is it trusted, and where is
 * it" without exposing the key. `lastSeenAt` is the last time the device
 * actually spoke, not the last time anybody looked at this list — a supervisor
 * deciding whether a handset is missing needs the former.
 */
export const scannerDeviceSchema = z.object({
    scannerDeviceId: z.string().uuid(),
    displayName: z.string(),
    deviceFingerprint: z.string(),
    status: z.enum(['ACTIVE', 'REVOKED']),
    gateId: z.string().uuid().nullable(),
    gateCode: z.string().nullable(),
    lastSeenAt: isoInstantSchema.nullable(),
    revokedAt: isoInstantSchema.nullable(),
    revokedReason: z.string().nullable(),
});
export const scannerDeviceListSchema = z.object({
    devices: z.array(scannerDeviceSchema),
});
// --- Refusals ----------------------------------------------------------------
/**
 * Why scanner provisioning refused, in a vocabulary a surface can act on.
 *
 * Exactly the reasons the provisioning surface actually emits, held to that by
 * `apps/api/test/unit/scanner-refusal-authority.test.ts` in both directions: a
 * reason produced and not declared fails, and a reason declared and not
 * produced fails. A vocabulary that only grows is one nobody trusts, and a
 * declared remedy nothing can produce is a remedy no operator is ever offered.
 *
 * **Four values were removed for a security reason, not for tidiness.** This
 * list previously declared `ACTIVATION_NOT_FOUND`, `ACTIVATION_EXPIRED`,
 * `ACTIVATION_ALREADY_CLAIMED` and `ACTIVATION_REVOKED`. The claim endpoint
 * deliberately answers all four with one `ACTIVATION_NOT_USABLE`, because
 * different answers would let anybody holding a code learn which of those it
 * is — an oracle about other people's activations. Restoring any of them as a
 * producer would reintroduce that oracle. They are gone, and they must not
 * come back.
 *
 * Two more were removed as never-shipped drift: `DEVICE_KEY_ALREADY_REGISTERED`
 * (the producer has always said `DEVICE_ALREADY_PROVISIONED`) and
 * `SCANNER_ALREADY_REVOKED` (re-revoking answers `RESOURCE_NOT_FOUND` with no
 * reason code at all). Neither was emitted in any commit in this repository's
 * history, so no client has received one and no stored row carries one.
 */
export const SCANNER_REFUSAL_REASONS = [
    /**
     * One answer for every reason a code does not work: unknown, consumed,
     * revoked, expired. Deliberately undifferentiated — see above.
     */
    'ACTIVATION_NOT_USABLE',
    /** This handset already holds a scanner identity. Reset it before re-provisioning. */
    'DEVICE_ALREADY_PROVISIONED',
    /**
     * Shared with the event surface, which refuses the same assignment for the
     * same reason. Declared here because scanner provisioning genuinely produces
     * it, not because this module owns it.
     */
    'GATE_NOT_AT_EVENT_VENUE',
];
export const scannerRefusalReasonSchema = z.enum(SCANNER_REFUSAL_REASONS);
export function asScannerRefusalReason(value) {
    const parsed = scannerRefusalReasonSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=scanner-schemas.js.map